import React, { useState, useMemo, useEffect } from 'react';
import { collection, onSnapshot, query, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';

const CATEGORY_META = {
  community:   { label: '👥 Community',   color: '#0369a1', bg: '#f0f9ff', border: '#bae6fd' },
  divorce:     { label: '💔 Divorce',     color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe' },
  foreclosure: { label: '🔥 Foreclosure', color: '#dc2626', bg: '#fff5f5', border: '#fecaca' },
  relocation:  { label: '🚛 Relocation',  color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
  downsizing:  { label: '🏠 Downsizing',  color: '#059669', bg: '#ecfdf5', border: '#a7f3d0' },
  estate:      { label: '🕊 Estate',       color: '#4b5563', bg: '#f9fafb', border: '#e5e7eb' },
};

const CATEGORY_FILTERS = [
  { key: 'all', label: 'All' },
  ...Object.entries(CATEGORY_META).map(([key, m]) => ({ key, label: m.label })),
];

function fmtCount(n) {
  if (n == null) return '—';
  if (n >= 1_000_000) return `${(n/1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${(n/1_000).toFixed(1)}K`;
  return String(n);
}

function CategoryBadge({ category }) {
  const m = CATEGORY_META[category] || { label: category, color: '#666', bg: '#f5f5f5', border: '#e5e5e5' };
  return (
    <span style={{ fontSize:10, fontWeight:700, padding:'2px 7px', borderRadius:20,
      color:m.color, background:m.bg, border:`1px solid ${m.border}`, whiteSpace:'nowrap' }}>
      {m.label}
    </span>
  );
}

function Toggle({ on, onChange }) {
  return (
    <button onClick={onChange} style={{
      width:38, height:22, borderRadius:11, border:'none', cursor:'pointer', flexShrink:0,
      background: on ? '#10b981' : '#d1d5db', position:'relative', transition:'background 0.15s',
    }}>
      <span style={{ position:'absolute', top:2, left: on ? 18 : 2, width:18, height:18, borderRadius:'50%',
        background:'#fff', boxShadow:'0 1px 3px rgba(0,0,0,0.2)', transition:'left 0.15s' }} />
    </button>
  );
}

function GroupRow({ entry, onToggle }) {
  return (
    <div style={{
      display:'flex', alignItems:'center', gap:14, padding:'14px 20px',
      borderBottom:'1px solid #f3f3f3',
    }}
      onMouseEnter={e => e.currentTarget.style.background='#fafafa'}
      onMouseLeave={e => e.currentTarget.style.background='transparent'}
    >
      <Toggle on={!!entry.scrapeEnabled} onChange={() => onToggle(entry.id, !entry.scrapeEnabled)} />

      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:3, flexWrap:'wrap' }}>
          <a href={entry.url} target="_blank" rel="noopener noreferrer"
            style={{ fontWeight:600, fontSize:14, color:'#0f0f0f', textDecoration:'none' }}>
            {entry.name}
          </a>
          <CategoryBadge category={entry.category} />
          {entry.stateAbbr && (
            <span style={{ fontSize:11, color:'#666', fontWeight:600, background:'#f3f4f6', padding:'1px 7px', borderRadius:10 }}>
              {entry.city ? `${entry.city}, ${entry.stateAbbr}` : entry.stateAbbr}
            </span>
          )}
          {entry.isPrivate && (
            <span style={{ fontSize:10, color:'#b45309', background:'#fef3c7', padding:'1px 6px', borderRadius:10, fontWeight:600 }}>
              Private
            </span>
          )}
          {entry.isJoined && (
            <span style={{ fontSize:10, color:'#065f46', background:'#d1fae5', padding:'1px 6px', borderRadius:10, fontWeight:600 }}>
              Joined
            </span>
          )}
        </div>
        <div style={{ display:'flex', gap:12, alignItems:'center', fontSize:11, color:'#999' }}>
          <span><strong style={{ color:'#666' }}>{fmtCount(entry.memberCount)}</strong> members</span>
          {entry.postsPerDay != null && <span>· {entry.postsPerDay}/day</span>}
          {entry.searchQuery && (
            <span style={{ fontStyle:'italic', color:'#bbb' }}>via "{entry.searchQuery}"</span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function FbDiscoveredGroupsDashboard() {
  const [groups, setGroups]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [stateFilter, setState] = useState('all'); // 'all' | 'MD' | 'DC' | 'VA'
  const [catFilter, setCat]     = useState('all');
  const [enabledOnly, setEO]    = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(query(collection(db, 'fbDiscoveredGroups')), snap => {
      setGroups(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return () => unsub();
  }, []);

  async function toggleScrape(id, val) {
    await updateDoc(doc(db, 'fbDiscoveredGroups', id), { scrapeEnabled: val });
  }

  const filtered = useMemo(() => {
    return groups
      .filter(g => {
        if (stateFilter !== 'all' && g.stateAbbr !== stateFilter) return false;
        if (catFilter   !== 'all' && g.category  !== catFilter)   return false;
        if (enabledOnly && !g.scrapeEnabled) return false;
        const q = search.toLowerCase();
        if (q && !g.name?.toLowerCase().includes(q) && !g.city?.toLowerCase().includes(q)) return false;
        return true;
      })
      .sort((a, b) => (b.memberCount || 0) - (a.memberCount || 0));
  }, [groups, stateFilter, catFilter, enabledOnly, search]);

  const enabledCount = groups.filter(g => g.scrapeEnabled).length;

  return (
    <div style={{ display:'flex', flexDirection:'column', minHeight:'100vh', background:'#fff',
      fontFamily:'-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>

      {/* Top header */}
      <div style={{ padding:'20px 28px', borderBottom:'1px solid #f3f3f3', display:'flex',
        flexDirection:'column', gap:12 }}>
        <div style={{ display:'flex', alignItems:'baseline', gap:12 }}>
          <h1 style={{ margin:0, fontSize:18, fontWeight:700 }}>Discovered Groups</h1>
          <span style={{ fontSize:12, color:'#aaa' }}>
            {loading ? '—' : `${groups.length} found · ${enabledCount} enabled for scraping`}
          </span>
        </div>

        <div style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
          <input
            placeholder="Search group name…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ flex:'1 1 280px', maxWidth:380, padding:'7px 13px', border:'1px solid #e8e8e8',
              borderRadius:8, fontSize:13, color:'#111', background:'#fff', outline:'none' }}
          />

          {/* State pills */}
          <div style={{ display:'flex', gap:6 }}>
            {['all','MD','DC','VA'].map(s => (
              <button key={s} onClick={() => setState(s)} style={{
                padding:'5px 11px', borderRadius:20, fontSize:12,
                fontWeight: stateFilter===s ? 700 : 500, cursor:'pointer',
                border: stateFilter===s ? '1.5px solid #111' : '1.5px solid #e5e5e5',
                background: stateFilter===s ? '#111' : '#fff',
                color: stateFilter===s ? '#fff' : '#555',
              }}>
                {s === 'all' ? 'All states' : s}
              </button>
            ))}
          </div>

          <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'#555', cursor:'pointer' }}>
            <input type="checkbox" checked={enabledOnly} onChange={e => setEO(e.target.checked)} />
            Enabled only
          </label>

          <span style={{ fontSize:13, color:'#bbb', marginLeft:'auto' }}>
            {filtered.length} match{filtered.length !== 1 ? 'es' : ''}
          </span>
        </div>

        {/* Category chips */}
        <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
          {CATEGORY_FILTERS.map(f => {
            const active = catFilter === f.key;
            return (
              <button key={f.key} onClick={() => setCat(f.key)} style={{
                padding:'5px 11px', borderRadius:20, fontSize:12,
                fontWeight: active ? 700 : 500, cursor:'pointer',
                border: active ? '1.5px solid #111' : '1.5px solid #e5e5e5',
                background: active ? '#111' : '#fff', color: active ? '#fff' : '#555',
              }}>{f.label}</button>
            );
          })}
        </div>
      </div>

      {/* List */}
      <div style={{ flex:1, overflowY:'auto' }}>
        {loading ? (
          <div style={{ padding:'80px 0', textAlign:'center', color:'#ddd', fontSize:14 }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding:'80px 0', textAlign:'center', color:'#ddd', fontSize:14 }}>
            No groups match. Run <code style={{ background:'#f3f4f6', padding:'2px 6px', borderRadius:4 }}>node discoverGroups.js</code> to populate.
          </div>
        ) : filtered.map(g => (
          <GroupRow key={g.id} entry={g} onToggle={toggleScrape} />
        ))}
      </div>
    </div>
  );
}
